/**
 * Signed change formatting with no "+0%" and no "-0%".
 *
 * Every "+3%" / "-1.2%" in the product goes through here. The value is
 * rounded to the precision it is shown at FIRST, and a rounded zero is
 * plain "0" (no sign), so -0.04 at one decimal never prints "-0.0%" and
 * 0.3 at whole percent never prints "+0%". Callers colour by
 * `changeSign` with the same digits, so a rounded zero is also neutral
 * gray instead of a red or green "0%".
 */

/** `v` rounded to `digits` decimals, with -0 folded to 0. */
export function roundChange(v: number, digits = 0): number {
  const f = 10 ** digits;
  const r = Math.round(v * f) / f;
  return r === 0 ? 0 : r;
}

/** -1 / 0 / 1 of the value AS DISPLAYED (after rounding to `digits`). */
export function changeSign(v: number | null | undefined, digits = 0): -1 | 0 | 1 {
  if (v == null || !Number.isFinite(v)) return 0;
  const r = roundChange(v, digits);
  return r > 0 ? 1 : r < 0 ? -1 : 0;
}

/**
 * "+3", "-1.2", "0". `format` renders the absolute rounded value (for
 * locale separators or trimmed decimals); default is `toFixed(digits)`.
 */
export function signedNumber(
  v: number,
  digits = 0,
  format?: (abs: number) => string,
): string {
  const r = roundChange(v, digits);
  if (r === 0) return "0";
  const abs = Math.abs(r);
  return `${r > 0 ? "+" : "-"}${format ? format(abs) : abs.toFixed(digits)}`;
}

/** "+3%", "-1.2%", "0%". Same rules as `signedNumber`. */
export function signedPct(
  v: number,
  digits = 0,
  format?: (abs: number) => string,
): string {
  return `${signedNumber(v, digits, format)}%`;
}

/** Trims trailing zeros: 1.50 -> "1.5", 2.0 -> "2". For `format`. */
export const trimDecimals = (abs: number): string => String(abs);
